import { ChangeDetectorRef, Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { ActivatedRoute, Router } from '@angular/router'

import { KirjanpidonProjekti } from '@lemon/core-firebase/model/kirjanpito-projektit'
import { FirestoreTosite, FirestoreTositteenAlkuperainenTiedosto, FirestoreTositteenKuva } from '@lemon/core-firebase/model/tosite'
import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'

import { TositeKatseleComponentData } from '../../_angular/_resolvers/tosite.resolve'
import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'
import { LEMONATOR_CF_API, LEMONATOR_HTTPS_IMAGES_API, LemonHttpService } from '../../_angular/service/lemon-http.service'
import { TositeKuvaService } from '../../_angular/service/tosite/tosite-kuva.service'

import { Observable, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'

import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { DateService } from '@lemon/core/service/date.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { KirjanpitoService } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { Asiakas, AsiakkaanMaksutapa } from '../../_jaettu-lemonator/model/asiakas'

import { MatDialog } from '@angular/material/dialog'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { LataaTositeLemonaidistaUudelleenRequest, LataaTositeLemonaidistaUudelleenResponse } from 'app/_jaettu-lemonator/model/tyojono'
import { TuontiKaynnistettyUudelleenDialog, TuontiKaynnistettyUudelleenDialogData } from './tuonti-kaynnistetty-uudelleen.dialog'

interface FirestoreTositteenAlkuperainenListausTiedosto extends FirestoreTositteenAlkuperainenTiedosto {
  dataUri: string
}

interface FirestoreKuitinLatausKuva extends FirestoreTositteenKuva {
  ladataan: boolean
  safeUrl: SafeUrl
  dataUri: string
}

@Component({
  templateUrl: './tosite.katsele.component.html',
  styleUrls: ['./tosite.katsele.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TositeSelailuKatseleComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>()

  VIRTUAL_SCROLL_THRESHOLD: number = 10 // Kun kuvien määrä on suurempi kuin tämä, käytetään virtual-scrollia. Muuten, käytetään tavallista for-loopia.

  tosite: FirestoreTosite = null
  ryhmitetytKuvat: FirestoreKuitinLatausKuva[][] = null
  alkuperaiset: FirestoreTositteenAlkuperainenTiedosto[] = null
  otsakeObservable: Observable<string> = null
  kirjanpidonProjektitObservable: Observable<KirjanpidonProjekti[]> = null
  projektiObservable: Observable<KirjanpidonProjekti> = null
  asiakas: Asiakas = null
  paluuUrl: string = null
  kuittiDataFirestoreUrl: string = null
  kirjattavaKuittiDataFirestoreUri: string = null
  kirjattavaKuittiDataFirestoreUrl: string = null
  kuittiDataFirestoreUri: string = null
  kuittiDataRealtimeDbUrl: string = null
  kuittiDataRealtimeDbUri: string = null
  binaryDataUrlFirebase: string = null
  binaryDataUrlGCP: string = null
  readonly naytaDebug = signal(false)
  sivunKorkeus: number = 1000

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private errorHandler: ErrorHandler,
    private sanitizer: DomSanitizer,
    private httpService: LemonHttpService,
    private tositeUriService: TositeUriService,
    private tositeKuvaService: TositeKuvaService,
    private asiakasService: AsiakasService,
    private dateService: DateService,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _ladataanService: LadataanService,
    private _copyService: KopioijaPalvelu,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _fileSaverService: FileSaverService,
    private _lemonatorFirebase: FirebaseLemonator,
    private _dialog: MatDialog,
    private _kirjanpitoService: KirjanpitoService,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {}

  kuvanLataaminenValmis(kuitti: FirestoreKuitinLatausKuva) {
    kuitti.ladataan = false
  }

  trackKuva(index: number, kuva: FirestoreKuitinLatausKuva): string {
    return kuva.avain
  }

  ngOnInit() {
    this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaTaiTukiObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(onDevaajaTaiTuki => {
      this.naytaDebug.set(onDevaajaTaiTuki)
    })

    this.kirjanpidonProjektitObservable = this._kirjanpitoService.kirjanpidonProjektitObservable

    this.route.data.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data: { data: TositeKatseleComponentData }) => {
      this.tosite = data.data.tosite
      this.asiakas = data.data.asiakas

      this.projektiObservable = this.tosite.projektiAvain ? this.kirjanpidonProjektitObservable.pipe(map(projektit => projektit?.find(p => p.avain === this.tosite.projektiAvain) ?? null)) : null

      this.ryhmitetytKuvat = this.annaJaRyhmitaKuvat(this.tosite)
      this.alkuperaiset = this.annaAlkuperaiset(this.tosite)

      this.kuittiDataRealtimeDbUri = this.tositeUriService.annaKuitinFirebaseUri(this.asiakas.asiakasId + '', this.tosite.localPvm.year, this.tosite.localPvm.month, this.tosite.kuvakansio)
      this.kuittiDataRealtimeDbUrl = this._debugService.createRealtimeDatabaseLinkLemonaid(this.kuittiDataRealtimeDbUri)

      this.kuittiDataFirestoreUri = this.tositeUriService.annaTositteenFirestoreUri(this.asiakas.avain, this.tosite.avain)
      this.kuittiDataFirestoreUrl = this._debugService.createFirestoreLinkLemonaid(this.kuittiDataFirestoreUri)

      this.kirjattavaKuittiDataFirestoreUri = this._kirjanpitoUriService.annaKirjattavanFirestoreTositteenUriAvaimella(this.asiakas.avain, this.tosite.avain)
      this.kirjattavaKuittiDataFirestoreUrl = this._debugService.createFirestoreLink(this.kirjattavaKuittiDataFirestoreUri)

      this.binaryDataUrlFirebase = this._debugService.annaFirebaseBinaryLinkLemonaidUsa(this.asiakas.asiakasId + '/' + this.tosite.kuvakansio)
      this.binaryDataUrlGCP = this._debugService.annaGCBinaryLinkLemonaidUsa(this.asiakas.asiakasId + '/' + this.tosite.kuvakansio)

      this.paluuUrl = 'asiakkaat/' + this.asiakas.avain + '/tositteet/selaa'
      this.otsakeObservable = this.asiakasService.nykyisenAsiakkaanJaKayttajienRoolienPerusteellaAktivoitavatMaksutavatObservable.pipe(
        map((maksutavat): string => {
          if (maksutavat) {
            let valittuMaksutapa: AsiakkaanMaksutapa = null
            for (const maksutapa of maksutavat) {
              if (maksutapa.tunniste === this.tosite.maksutapa) {
                valittuMaksutapa = maksutapa
              }
            }
            return (valittuMaksutapa === null ? 'Ei maksutapaa' : valittuMaksutapa.nimi) + ' ' + this.dateService.muotoilePaikallinenPaiva(this.tosite.localPvm, 'fi')
          }
          return ''
        }),
      )
      this._changeDetectorRef.markForCheck()
    })
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  private annaAlkuperaiset(tosite: FirestoreTosite): FirestoreTositteenAlkuperainenListausTiedosto[] {
    if (tosite.alkuperaiset) {
      const alkuperaiset: FirestoreTositteenAlkuperainenListausTiedosto[] = []
      for (const alkuperaisenAvain of Object.keys(tosite.alkuperaiset)) {
        const alkuperainen: FirestoreTositteenAlkuperainenListausTiedosto = this._copyService.cloneObjectDeep(this.tosite.alkuperaiset[alkuperaisenAvain])
        alkuperainen.dataUri = this.tositeUriService.annaCloudStorageAlkuperainenUriAsiakasIdlle(this.asiakas.asiakasId + '', alkuperainen)
        alkuperaiset.push(alkuperainen)
      }
      return alkuperaiset.sort((a, b) => {
        return (a.jarjestys ?? 0) - (b.jarjestys ?? 0)
      })
    }
    return null
  }

  /** Antaa ja ryhmittää kuvien safeURL:it joko alkuperaisenAvaimen vai kuvan avaimen perusteella */
  private annaJaRyhmitaKuvat(tosite: FirestoreTosite): FirestoreKuitinLatausKuva[][] {
    const ryhmitetytKuvatMap: Map<string, FirestoreKuitinLatausKuva[]> = new Map<string, FirestoreKuitinLatausKuva[]>()

    const kuvat: FirestoreKuitinLatausKuva[] = []
    for (const kuvanAvain of Object.keys(tosite.kuvat)) {
      const kuva: FirestoreKuitinLatausKuva = this._copyService.cloneObjectDeep(tosite.kuvat[kuvanAvain])

      // kuva.safeUrl = '/assets/noimage.png'
      kuva.ladataan = true
      kuvat.push(kuva)

      const kavain = kuva.avain + '_quality'
      const kuvanUrl = this.tositeUriService.annaCloudStorageKuvaUri(this.asiakas.asiakasId + '', tosite.kuvakansio, kavain, 'webp')
      kuva.safeUrl = LEMONATOR_HTTPS_IMAGES_API + '/api/1/kuvat/' + kuvanUrl

      const ryhmanAvain = kuva.alkuperaisenAvain ?? kuva.avain
      if (!ryhmitetytKuvatMap.has(ryhmanAvain)) {
        ryhmitetytKuvatMap.set(ryhmanAvain, [])
      }
      ryhmitetytKuvatMap.get(ryhmanAvain).push(kuva)
    }

    for (const ryhmitetytKuvat of ryhmitetytKuvatMap.values()) {
      ryhmitetytKuvat.sort((a, b): number => {
        return a.jarjestys < b.jarjestys ? -1 : a.jarjestys > b.jarjestys ? 1 : 0
      })
    }

    return Array.from(ryhmitetytKuvatMap.values()).sort((a, b) => (a[0].jarjestys ?? 0) - (b[0].jarjestys ?? 0))
  }

  lataaAlkuperainen(alkuperainen: FirestoreTositteenAlkuperainenTiedosto) {
    this._ladataanService.aloitaLataaminen()

    const uri = this.tositeUriService.annaCloudStorageAlkuperainenUriAsiakasIdlle(this.asiakas.asiakasId + '', alkuperainen)
    return this.httpService
      .getBinary('/kuittiLataaAlkuperainen?a=' + encodeURIComponent('/api/1/kuitit/kuvat/alkuperainen/' + uri), LEMONATOR_CF_API)
      .then(result => {
        this._ladataanService.lopetaLataaminen()
        this._fileSaverService.saveAs(result, alkuperainen.nimi)
      })
      .catch(err => {
        this._ladataanService.lopetaLataaminen()
        this.errorHandler.handleError(err)
      })
  }

  async reloadTosite() {
    this._ladataanService.aloitaLataaminen()

    try {
      const reqData: LataaTositeLemonaidistaUudelleenRequest = {
        asiakasId: '' + this.asiakas.asiakasId,
        asiakasAvain: this.asiakas.avain,
        tositeAvain: this.tosite.avain,
      }
      const resp = await this._lemonatorFirebase.functionsCall<LataaTositeLemonaidistaUudelleenRequest, LataaTositeLemonaidistaUudelleenResponse>('lataaTositeLemonaidistaUudelleen', reqData, {
        timeout: 540 * 1000,
      })

      if (!resp || resp.e) {
        throw new Error('Failed! ' + (resp?.e ?? 'No response'))
      }

      this._dialog.open<TuontiKaynnistettyUudelleenDialog, TuontiKaynnistettyUudelleenDialogData>(TuontiKaynnistettyUudelleenDialog, {
        data: { uri: resp.uri },
      })
    } catch (err) {
      this.errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }
}
